import { Drawer, Flex, Icon, Portal, Text } from '@chakra-ui/react'
import { X } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { ApiAccessPage } from '~/pages/ApiAccess/ApiAccessPage'
import { DeviceEditPage, DeviceNewPage, DevicesPage } from '~/pages/Devices'
import { ExtensionsPage } from '~/pages/Extensions'
import { PbxHeaderSwitcher } from '~/pages/Home/TopNav/PbxHeaderSwitcher'
import { PbxLocationsSettingsPage } from '~/pages/Locations'
import {
  MessagingGroupDetailPage,
  MessagingGroupsPage,
} from '~/pages/MessagingGroups'
import { MusicOnHoldSettingsPage } from '~/pages/MusicOnHold'
import { PbxSettingsSettingsPage } from '~/pages/PbxSettings'
import { PhoneNumberSettingsPage } from '~/pages/PhoneNumbers'
import { ScheduleDetailPage } from '~/pages/Schedules/ScheduleDetailPage'
import { SchedulesListPage } from '~/pages/Schedules/SchedulesListPage'
import {
  useSettingsPageLayoutStyle,
  useSettingsTabStyle,
} from '~/pages/Settings/componentStyles/SettingsPage.style'
import { SoundClipSettingsPage } from '~/pages/SoundClips'
import { SwitchboardEditView, SwitchboardPage } from '~/pages/Switchboard'
import { SystemSettingsSettingsPage } from '~/pages/SystemSettings'
import {
  UserGroupAddPage,
  UserGroupEditPage,
  UserGroupsPage,
} from '~/pages/UserGroups'
import { UsersPage } from '~/pages/Users/UsersPage'
import { WebhooksDetailPage } from '~/pages/Webhooks/WebhooksDetailPage'
import { WebhooksPage } from '~/pages/Webhooks/WebhooksPage'
import { useGetPbxList } from '~/queries/pbxQueries'
import { isUserAdmin } from '~/utils/auth'

import { getAdminReturnTo } from './adminReturnTo'
import { getInitialAdminMenu, saveLastAdminMenu } from './lastAdminMenu'
import {
  ADMIN_MENU_VALUES,
  type AdminMenuGroup,
  type AdminMenuItem,
  type AdminMenuValue,
  useAdminMenuItems,
} from './useAdminMenuItems'

type AdminTabItemProps = {
  menu: AdminMenuItem
  isSelected: boolean
  onClick: () => void
}

const AdminTabItem = ({ menu, isSelected, onClick }: AdminTabItemProps) => {
  const tabStyles = useSettingsTabStyle()
  const dataSelectedProps = { 'data-selected': isSelected ? '' : undefined }
  const selectedProps = {
    'aria-current': isSelected ? ('page' as const) : undefined,
    ...dataSelectedProps,
  }

  return (
    <Flex
      as="button"
      css={tabStyles.container}
      {...selectedProps}
      onClick={onClick}
      title={menu.label}
    >
      <Icon as={menu.icon} {...dataSelectedProps} css={tabStyles.icon} />
      <Text {...dataSelectedProps} css={tabStyles.titleText}>
        {menu.label}
      </Text>
    </Flex>
  )
}

type AdminMenuSectionProps = {
  group: AdminMenuGroup
  showDivider: boolean
  selectedMenu: string
  onSelect: (item: AdminMenuItem) => void
}

const AdminMenuSection = ({
  group,
  showDivider,
  selectedMenu,
  onSelect,
}: AdminMenuSectionProps) => {
  return (
    <Flex
      direction="column"
      w="100%"
      gap="0.25rem"
      pt={showDivider ? '0.75rem' : 0}
      mt={showDivider ? '0.75rem' : 0}
      borderTop={showDivider ? '1px solid' : undefined}
      borderColor={showDivider ? 'border.default' : undefined}
    >
      {group.label && (
        <Text
          px="0.75rem"
          pb="0.25rem"
          fontSize="xs"
          fontWeight="semibold"
          color="fg.muted"
          textTransform="uppercase"
          letterSpacing="0.05em"
        >
          {group.label}
        </Text>
      )}
      {group.items.map((item) => (
        <AdminTabItem
          key={item.value}
          menu={item}
          isSelected={selectedMenu === item.value}
          onClick={() => onSelect(item)}
        />
      ))}
    </Flex>
  )
}

export const AdminPanel = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const layoutStyles = useSettingsPageLayoutStyle()
  const { adminMenuItems, adminMenuGroups } = useAdminMenuItems()
  const { pbx, userRoles } = useUserState()

  const { data: pbxListData } = useGetPbxList({
    apiParams: { limit: 1 },
  })
  const hasMultiplePbxs = (pbxListData?.count ?? 0) > 1

  const isAdmin = isUserAdmin(userRoles)
  const selectedMenu = resolveSelectedMenu(location.pathname)
  const visibleMenuValues = new Set(adminMenuItems.map((m) => m.value))

  const isMenuAvailable =
    selectedMenu !== '' &&
    ADMIN_MENU_VALUES.has(selectedMenu) &&
    visibleMenuValues.has(selectedMenu as AdminMenuValue)

  React.useEffect(() => {
    if (!isAdmin) return
    if (isMenuAvailable) {
      saveLastAdminMenu(selectedMenu)
      return
    }
    const initialMenu = getInitialAdminMenu(adminMenuItems.map((m) => m.value))
    if (initialMenu) {
      navigate(getRouteForMenu(initialMenu), { replace: true })
    }
  }, [isAdmin, isMenuAvailable, selectedMenu, adminMenuItems, navigate])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  const handleClose = () => {
    navigate(getAdminReturnTo())
  }

  const handleMenuSelect = (item: AdminMenuItem) => {
    navigate(getRouteForMenu(item.value))
  }

  const content = isMenuAvailable
    ? resolveContent(selectedMenu, location.pathname, params)
    : null

  return (
    <Drawer.Root
      open
      onOpenChange={(details) => {
        if (!details.open) handleClose()
      }}
      placement="bottom"
      size="full"
    >
      <Portal>
        <Drawer.Backdrop bg="bg.overlay" />
        <Drawer.Positioner>
          <Drawer.Content
            bg="bg.default"
            borderTopRadius="xl"
            display="flex"
            flexDirection="column"
            h="calc(100vh - 2rem)"
            mt="2rem"
          >
            <Flex
              align="center"
              justify="space-between"
              px={6}
              h="4.5rem"
              minH="4.5rem"
              borderBottom="2px solid"
              borderColor="border.default"
            >
              <Drawer.Title fontSize="lg" fontWeight="bold" color="fg.default">
                {t('baseSettings.admin', 'Admin')}
              </Drawer.Title>
              <Flex align="center" gap={3}>
                <PbxHeaderSwitcher />
                {!hasMultiplePbxs && pbx?.name && (
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    {pbx.name}
                  </Text>
                )}
                <Flex
                  as="button"
                  aria-label={t('baseSettings.closeAdmin', 'Close admin')}
                  title={t('baseSettings.closeAdmin', 'Close admin')}
                  align="center"
                  justify="center"
                  boxSize={8}
                  borderRadius="md"
                  cursor="pointer"
                  _hover={{ bg: 'bg.muted' }}
                  onClick={handleClose}
                >
                  <Icon boxSize={5} color="fg.muted" aria-hidden>
                    <X size={20} />
                  </Icon>
                </Flex>
              </Flex>
            </Flex>

            <Flex flex={1} minH={0} overflow="hidden">
              <Flex
                direction="column"
                w="15rem"
                minW="15rem"
                borderRight="2px solid"
                borderColor="border.default"
                bg="bg.default"
                overflowY="auto"
              >
                <Flex css={layoutStyles.tabListWrapper}>
                  <Flex
                    as="nav"
                    aria-label={t(
                      'baseSettings.adminSections',
                      'Admin sections',
                    )}
                    css={layoutStyles.tabList}
                  >
                    {adminMenuGroups.map((group, groupIndex) => (
                      <AdminMenuSection
                        key={group.id}
                        group={group}
                        showDivider={groupIndex > 0}
                        selectedMenu={selectedMenu}
                        onSelect={handleMenuSelect}
                      />
                    ))}
                  </Flex>
                </Flex>
              </Flex>

              <Flex
                flex={1}
                direction="column"
                overflow="auto"
                bg="bg.muted"
                minH={0}
              >
                {content}
              </Flex>
            </Flex>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

function resolveSelectedMenu(pathname: string): string {
  const adminPath = pathname.replace(/^\/admin\/?/, '')
  const segment = adminPath.split('/')[0]
  return segment || ''
}

function getRouteForMenu(value: string): string {
  return `/admin/${value}`
}

type RouteParams = Record<string, string | undefined>

function resolveContent(
  menu: string,
  pathname: string,
  params: RouteParams,
): React.ReactNode {
  switch (menu) {
    case 'users':
      return <UsersPage />

    case 'user-groups':
      if (pathname.endsWith('/user-groups/add')) return <UserGroupAddPage />
      if (params.userGroupId && pathname.endsWith('/edit'))
        return <UserGroupEditPage />
      return <UserGroupsPage />

    case 'schedules':
      if (params.id) return <ScheduleDetailPage />
      return <SchedulesListPage />

    case 'switchboards':
      if (params.extensionDialPlan) return <SwitchboardEditView />
      return <SwitchboardPage />

    case 'devices':
      if (pathname.includes('/admin/devices/new')) return <DeviceNewPage />
      if (params.deviceId) return <DeviceEditPage />
      return <DevicesPage />

    case 'extensions':
      return <ExtensionsPage />

    case 'messaging-groups':
      if (params.groupId) return <MessagingGroupDetailPage />
      return <MessagingGroupsPage />

    case 'sound-clips':
      return (
        <SoundClipSettingsPage
          soundClipId={params.soundClipId}
          isCreating={pathname.endsWith('/sound-clips/new')}
        />
      )

    case 'music-on-hold':
      return <MusicOnHoldSettingsPage playlistId={params.playlistId} />

    case 'phone-numbers':
      return <PhoneNumberSettingsPage phoneNumberId={params.phoneNumberId} />

    case 'locations':
      return <PbxLocationsSettingsPage />

    case 'system-settings':
      return <SystemSettingsSettingsPage />

    case 'pbx-settings':
      return <PbxSettingsSettingsPage />

    case 'integrations':
      if (params.integrationId) return <WebhooksDetailPage />
      return <WebhooksPage />

    case 'api-access':
      return <ApiAccessPage />

    default:
      return null
  }
}
